import { useMemo, useState } from 'react';
import { useLoaderData } from 'react-router';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import { RoccoLink } from '@rocco/components/link';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import {
    DetailTileCard,
    detailCta,
    type IslandMode,
    useIslandSection,
} from '~/ui-rocco/pages/detail-shared';
import { CONTENT_CAP_PADDED } from '~/ui-rocco/pages/detail-shared';
import { SectionLabel } from '~/ui-rocco/pages/home-page/components/SectionLabel';
import { useProjectAccordionRows } from '../hooks/use-project-accordion-rows';
import { useInfiniteScrollSentinel } from '~/ui-rocco/pages/category-page/hooks/useInfiniteScrollSentinel';
import { useSimilarProjects } from './use-similar-projects';

/**
 * P10 — Similar Projects. Category-wide projects (any professional) in the same
 * category, mirroring the product page's "More in {category}" block: a numbered
 * section divider, the heading with the category set apart as a link, then a
 * 3-up grid of wide tiles on the dark editorial background, growing by "Load
 * More". Self-hides when there are no similar projects.
 */
export const SimilarProjectsSection = () => {
    const { project } = useLoaderData<typeof loader>();
    const { mobile } = useAppDisplayModeContext();
    const { rows } = useProjectAccordionRows();
    const { tiles, hasMore, isLoading, loadMore } = useSimilarProjects();
    const category = project.Categories?.[0]?.PrettyTitle;

    // Same hybrid as the product page: Load More is a real, crawlable control
    // for the first step, and arms infinite scroll for everything after it, so
    // the reader opts in once rather than the page growing unasked.
    const [infiniteEnabled, setInfiniteEnabled] = useState(false);
    // The shared sentinel keeps one observer for the element's lifetime. It
    // must not be rebuilt per load: a fresh observer reports an
    // already-intersecting target immediately, so tearing it down and
    // recreating it each cycle fetched again with no reader input at all. The
    // margin also has to be something one page clears, or the sentinel never
    // leaves the zone and paging runs to the end of the category on its own.
    const setSentinelRef = useInfiniteScrollSentinel({
        enabled: infiniteEnabled && hasMore && !isLoading,
        onTrigger: () => loadMore(),
        rootMarginPx: 600,
    });

    // Magic Island: scrolling through this block hands the island a link out to
    // the category directory (the last breadcrumb), mirroring the product page's
    // "See more products in {category}" link.
    const breadcrumbs = project.Breadcrumbs;
    const categoryLink = breadcrumbs[breadcrumbs.length - 1]?.href;
    const similarMode = useMemo<IslandMode>(
        () => ({
            kind: 'link',
            label: `See more projects in ${category ?? 'this category'}`,
            to: categoryLink ?? '',
        }),
        [category, categoryLink],
    );
    // Desktop only: on mobile the persistent Book-a-Meeting pill stays put
    // rather than being swapped for this category link (which pulls the reader
    // off the enquiry path). Desktop has no purchase pill, so the link earns it.
    const islandRef = useIslandSection<HTMLElement>(similarMode, {
        enabled: !mobile && tiles.length > 0 && Boolean(categoryLink),
    });

    if (tiles.length === 0) return null;

    // Continues the page's numbered sequence: accordion rows → About Supplier
    // (rows + 1) → Similar Projects (rows + 2).
    const number = String(rows.length + 2).padStart(2, '0');

    return (
        <section
            ref={islandRef}
            className="bg-transparent text-white max-md:px-4 md:px-[3rem]"
        >
            <div className={CONTENT_CAP_PADDED}>
                <div className="border-t border-white/20 max-md:pt-3 md:pt-[1.062rem]">
                    <SectionLabel className="tracking-normal text-white max-md:text-[0.875rem]">
                        ({number} — Similar Projects)
                    </SectionLabel>
                </div>

                {/* The product page's Similar Products head: the title's
                    scale rather than the 200px statement, with the category
                    set apart in grey because it is a link. */}
                <h2 className="m-0 font-sans font-normal text-white max-md:mt-6 max-md:text-[2rem] max-md:leading-[1.08] max-md:tracking-[-0.02em] md:mt-[6rem] md:text-[3.9rem] md:leading-[1.08] md:tracking-[-0.125rem]">
                    More in{' '}
                    {categoryLink ? (
                        <RoccoLink
                            to={categoryLink}
                            className="text-[#a7a7a8] transition-colors hover:text-white"
                        >
                            {category}
                        </RoccoLink>
                    ) : (
                        <span className="text-[#a7a7a8]">{category}</span>
                    )}{' '}
                    category
                </h2>

                <div className="grid grid-cols-2 max-md:mt-6 max-md:gap-x-3.5 max-md:gap-y-[3.6875rem] md:mt-[3rem] md:grid-cols-3 md:gap-x-[1.5rem] md:gap-y-[6.25rem]">
                    {tiles.map((tile) => (
                        <DetailTileCard
                            key={tile.id}
                            tile={tile}
                            theme="dark"
                            mediaClassName="md:aspect-[37/27]"
                            textClassName="md:text-[1rem] md:leading-[1.3]"
                        />
                    ))}
                </div>

                {hasMore && !infiniteEnabled ? (
                    <div className="mt-[3rem] flex justify-center max-md:mt-10">
                        <button
                            type="button"
                            onClick={() => {
                                setInfiniteEnabled(true);
                                loadMore();
                            }}
                            disabled={isLoading}
                            className={detailCta(
                                'filled-dark',
                                'disabled:opacity-50 max-md:h-[3.125rem] max-md:w-full md:min-w-[14.4rem]',
                            )}
                        >
                            {isLoading ? 'Loading…' : 'Load More'}
                        </button>
                    </div>
                ) : null}
                {hasMore && infiniteEnabled ? (
                    <div
                        ref={setSentinelRef}
                        aria-hidden="true"
                        className="h-px"
                    />
                ) : null}
            </div>
        </section>
    );
};
